import React, { useMemo } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  LinearProgress,
  Stack,
  Chip,
  Avatar,
  Button,
} from '@mui/material';
import {
  Psychology,
  VideoLibrary,
  CardMembership,
  ContactSupport,
  Forum,
  LocalLibrary,
  Download,
} from '@mui/icons-material';
import CheckCircle from '@mui/icons-material/CheckCircle';
import * as XLSX from 'xlsx';

export default function PlatformIntelligenceDashboard({ platformData }) {
  const stats = useMemo(() => {
    return {
      intel: platformData?.intelligence || {},
      content: platformData?.content || {},
      subs: platformData?.subscriptions || {},
      clients: platformData?.clients || {},
      msg: platformData?.messaging || {},
      learning: platformData?.learning || {},
    };
  }, [platformData]);

  const num = (value, decimals = 0) => {
    const parsed = parseFloat(value);
    return isNaN(parsed) ? 0 : parsed.toFixed(decimals);
  };

  const handleExportExcel = () => {
    const wb = XLSX.utils.book_new();

    const resumeData = [
      {
        Indicateur: 'Santé Générale de la Plateforme',
        Valeur: `${num(stats.intel.platform_health_score, 1)}%`,
      },
      {
        Indicateur: 'Utilisateurs Totaux',
        Valeur: stats.intel.total_users || 0,
      },
      {
        Indicateur: 'Nombre de Clients',
        Valeur: stats.intel.clients_count || 0,
      },
      {
        Indicateur: "Score d'Activité Général",
        Valeur: stats.intel.activity_score || 0,
      },
      {
        Indicateur: 'Rendement Moyen par Profil Actif',
        Valeur: `${num(stats.intel.revenue_per_active_user, 2)} €`,
      },
    ];
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(resumeData),
      'Synthèse Plateforme'
    );

    const mediaData = [
      {
        Métrique: 'Vidéos Publiées',
        Valeur: stats.content.published_videos || 0,
        Détail: `${stats.content.published_rate}% du catalogue`,
      },
      {
        Métrique: 'Taux de Monétisation',
        Valeur: `${num(stats.content.monetization_rate, 1)}%`,
        Détail: `${stats.content.paid_videos || 0} vidéos payantes`,
      },
      {
        Métrique: 'Vues Cumulées',
        Valeur: stats.content.video_views || 0,
        Détail: `${num(stats.content.avg_views_per_video, 1)} vues en moyenne / vidéo`,
      },
    ];
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(mediaData),
      'Bibliothèque Médias'
    );

    XLSX.writeFile(
      wb,
      `Rapport_Intelligence_Plateforme_${new Date().getFullYear()}.xlsx`
    );
  };

  return (
    <Box
      sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 3.5 }}
    >
      {/* Alignement parfait du bouton d'exportation en haut à droite */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          width: '100%',
          mt: -7,
          mb: 2,
        }}
      >
        <Button
          variant="contained"
          size="small"
          startIcon={<Download />}
          onClick={handleExportExcel}
           sx={{
          textTransform: 'none',
          fontWeight: 700,
          fontSize: '0.85rem',
          bgcolor: '#10b981',
          borderRadius: '8px',
          px: 2.5,
          py: 0.9,
          boxShadow: '0 1px 2px 0 rgba(0,0,0,0.05)',
          '&:hover': { bgcolor: '#059669' },
        }}
        >
          Exporter KPI Globaux
        </Button>
      </Box>

      {/* LIGNE 1 : INFRASTRUCTURE PRINCIPALE (SANTÉ GLOBALE ET RÉPARTITION) */}
      <Grid container spacing={3}>
        {/* 1. Intelligence Plateforme */}
        <Grid item xs={12} md={6}>
          <Paper
            sx={{
              p: 3,
              borderRadius: 4,
              bgcolor: '#ffffff',
              border: '1px solid #f1f5f9',
              height: '100%',
              boxSizing: 'border-box',
              boxShadow: 'none',
            }}
          >
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ mb: 3 }}
            >
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Avatar
                  sx={{ bgcolor: '#eff6ff', color: '#2563eb', borderRadius: 2 }}
                >
                  <Psychology />
                </Avatar>
                <Box>
                  <Typography
                    variant="subtitle1"
                    fontWeight={800}
                    color="#0f172a"
                  >
                    Intelligence Plateforme
                  </Typography>
                  <Typography variant="caption" color="#64748b">
                    Analyse globale de l'écosystème
                  </Typography>
                </Box>
              </Stack>
              <Box sx={{ textAlign: 'right' }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: '#64748b',
                    fontWeight: 600,
                    display: 'block',
                    textTransform: 'uppercase',
                  }}
                >
                  Santé Générale
                </Typography>
                <Typography
                  variant="h5"
                  sx={{ fontWeight: 900, color: '#10b981' }}
                >
                  {num(stats.intel.platform_health_score, 1)}%
                </Typography>
              </Box>
            </Stack>

            <Box sx={{ mb: 3 }}>
              <LinearProgress
                variant="determinate"
                value={parseFloat(stats.intel.platform_health_score || 0)}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  bgcolor: '#e2e8f0',
                  '& .MuiLinearProgress-bar': { bgcolor: '#10b981' },
                }}
              />
            </Box>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: 2,
                bgcolor: '#f8fafc',
                p: 2,
                borderRadius: 3,
              }}
            >
              <Box>
                <Typography variant="caption" color="#64748b" display="block">
                  Utilisateurs Totaux
                </Typography>
                <Typography variant="h6" fontWeight={800} color="#0f172a">
                  {stats.intel.total_users || 0}
                </Typography>
                <Typography variant="caption" color="#94a3b8">
                  {stats.intel.clients_count || 0} clients
                </Typography>
              </Box>
              <Box sx={{ borderLeft: '1px solid #e2e8f0', pl: 2 }}>
                <Typography variant="caption" color="#64748b" display="block">
                  Score Activité
                </Typography>
                <Typography variant="h6" fontWeight={800} color="#6366f1">
                  {stats.intel.activity_score || 0}
                </Typography>
                <Typography variant="caption" color="#94a3b8">
                  Intensité d'usage
                </Typography>
              </Box>
              <Box sx={{ borderLeft: '1px solid #e2e8f0', pl: 2 }}>
                <Typography variant="caption" color="#64748b" display="block">
                  Rendement Moyen
                </Typography>
                <Typography variant="h6" fontWeight={800} color="#0f172a">
                  {num(stats.intel.revenue_per_active_user, 2)} €
                </Typography>
                <Typography variant="caption" color="#94a3b8">
                  Par profil actif
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/* 2. Répartition des Catégories */}
        <Grid item xs={12} md={5}>
          <Paper
            sx={{
              p: 3,
              borderRadius: 4,
              bgcolor: '#ffffff',
              border: '1px solid #f1f5f9',
              height: '100%',
              boxSizing: 'border-box',
              boxShadow: 'none',
            }}
          >
            <Typography
              variant="subtitle1"
              fontWeight={800}
              color="#0f172a"
              sx={{ mb: 2.5 }}
            >
              Répartition Catégories ({stats.content.categories || 0})
            </Typography>
            <Stack
              spacing={2}
              sx={{ maxHeight: 190, overflowY: 'auto', pr: 0.5 }}
            >
              {(stats.content.offers_by_category || []).map((cat) => (
                <Box key={cat.category_id}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mb: 0.5,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: '0.78rem',
                        fontWeight: 600,
                        color: '#334155',
                      }}
                      noWrap
                    >
                      {cat.title && cat.title.length > 15
                        ? `${cat.title.substring(0, 15)}...`
                        : cat.title}
                    </Typography>

                    <Typography
                      sx={{
                        fontSize: '0.75rem',
                        fontWeight: 700,
                        color: '#4f46e5',
                      }}
                    >
                      {cat.offers_count} off. ({cat.share_percent}%)
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={cat.share_percent || 0}
                    sx={{
                      height: 4,
                      borderRadius: 2,
                      bgcolor: '#f1f5f9',
                      '& .MuiLinearProgress-bar': { bgcolor: '#818cf8' },
                    }}
                  />
                </Box>
              ))}
            </Stack>
          </Paper>
        </Grid>

        {/* 3. Bibliothèque & Contenu Médias */}
        <Grid item xs={12} md={6}>
          <Paper
            sx={{
              p: 3,
              borderRadius: 4,
              border: '1px solid #f1f5f9',
              bgcolor: '#ffffff',
              height: '100%',
              boxSizing: 'border-box',
              boxShadow: 'none',
            }}
          >
            <Stack
              direction="row"
              spacing={1.5}
              alignItems="center"
              sx={{ mb: 2.5 }}
            >
              <Avatar
                sx={{
                  bgcolor: '#fff1f2',
                  color: '#f43f5e',
                  borderRadius: 2,
                  width: 36,
                  height: 36,
                }}
              >
                <VideoLibrary sx={{ fontSize: 18 }} />
              </Avatar>
              <Typography variant="subtitle2" fontWeight={700} color="#0f172a">
                Bibliothèque & Contenu Médias
              </Typography>
            </Stack>
            <Stack spacing={2}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Typography variant="caption" color="#64748b">
                  Vidéos Publiées
                </Typography>
                <Typography variant="body2" fontWeight={700}>
                  {stats.content.published_videos || 0}{' '}
                  <Box
                    component="span"
                    sx={{
                      fontWeight: 400,
                      color: '#94a3b8',
                      fontSize: '0.75rem',
                    }}
                  >
                    ({stats.content.published_rate}%)
                  </Box>
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Typography variant="caption" color="#64748b">
                  Taux de Monétisation du Catalogue
                </Typography>
                <Typography variant="body2" fontWeight={700} color="#f43f5e">
                  {num(stats.content.monetization_rate, 1)}%
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Typography variant="caption" color="#64748b">
                  Volume de Vues Cumulées
                </Typography>
                <Typography variant="body2" fontWeight={700}>
                  {stats.content.video_views || 0}{' '}
                  <Box
                    component="span"
                    sx={{
                      fontWeight: 400,
                      color: '#94a3b8',
                      fontSize: '0.75rem',
                    }}
                  >
                    ({num(stats.content.avg_views_per_video, 1)}/vid)
                  </Box>
                </Typography>
              </Box>
              <Box
                sx={{
                  bgcolor: '#fff5f5',
                  p: 1.2,
                  borderRadius: 2,
                  textAlign: 'center',
                  mt: 1,
                }}
              >
                <Typography
                  sx={{
                    fontSize: '0.72rem',
                    color: '#e11d48',
                    fontWeight: 600,
                  }}
                >
                  Engagement Médias global :{' '}
                  {stats.content.content_engagement_score} pts
                </Typography>
              </Box>
            </Stack>
          </Paper>
        </Grid>

        {/* 4. Performances Commerciales */}
        <Grid item xs={12} md={6}>
          <Paper
            sx={{
              p: 3,
              borderRadius: 4,
              border: '1px solid #f1f5f9',
              bgcolor: '#ffffff',
              height: '100%',
              boxSizing: 'border-box',
              boxShadow: 'none',
            }}
          >
            <Stack
              direction="row"
              spacing={1.5}
              alignItems="center"
              sx={{ mb: 2.5 }}
            >
              <Avatar
                sx={{
                  bgcolor: '#f0fdf4',
                  color: '#16a34a',
                  borderRadius: 2,
                  width: 36,
                  height: 36,
                }}
              >
                <CardMembership sx={{ fontSize: 18 }} />
              </Avatar>
              <Typography variant="subtitle2" fontWeight={700} color="#0f172a">
                Performances Commerciales
              </Typography>
            </Stack>
            <Stack spacing={2}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Typography variant="caption" color="#64748b">
                  Volume de Ventes (Abonn)
                </Typography>
                <Typography variant="body2" fontWeight={700}>
                  {stats.subs.total_subscriptions || 0}{' '}
                  <Box
                    component="span"
                    sx={{
                      fontWeight: 400,
                      color: '#94a3b8',
                      fontSize: '0.75rem',
                    }}
                  >
                    ({stats.subs.plans || 0} plans)
                  </Box>
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Typography variant="caption" color="#64748b">
                  Chiffre d'Affaires Global
                </Typography>
                <Typography variant="body2" fontWeight={800} color="#16a34a">
                  {num(stats.subs.total_revenue, 2)} €
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Typography variant="caption" color="#64748b">
                  Revenu Moyen / Client Unique
                </Typography>
                <Typography variant="body2" fontWeight={700}>
                  {num(stats.subs.revenue_per_client, 2)} €
                </Typography>
              </Box>
              <Box
                sx={{
                  bgcolor: '#f0fdf4',
                  p: 1.2,
                  borderRadius: 2,
                  textAlign: 'center',
                  mt: 1,
                }}
              >
                <Typography
                  sx={{
                    fontSize: '0.72rem',
                    color: '#15803d',
                    fontWeight: 600,
                  }}
                >
                  Taux conversion abonnements :{' '}
                  {stats.intel.subscription_conversion_rate}%
                </Typography>
              </Box>
            </Stack>
          </Paper>
        </Grid>
      </Grid>

      {/* LIGNE 2 : DEUX GRANDES CARTES COMPOSITES (MÉDIAS ET COMMERCE EN md={6}) */}
      <Grid container spacing={3}></Grid>

      {/* LIGNE 3 : COMMUNICATION, MESSAGERIE ET RELATION CLIENTS (md={6}) */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper
            sx={{
              p: 3,
              borderRadius: 4,
              border: '1px solid #f1f5f9',
              bgcolor: '#ffffff',
              height: '100%',
              boxSizing: 'border-box',
              boxShadow: 'none',
            }}
          >
            <Stack
              direction="row"
              spacing={1.5}
              alignItems="center"
              sx={{ mb: 2.5 }}
            >
              <Avatar
                sx={{
                  bgcolor: '#faf5ff',
                  color: '#7c3aed',
                  borderRadius: 2,
                  width: 36,
                  height: 36,
                }}
              >
                <Forum sx={{ fontSize: 18 }} />
              </Avatar>
              <Typography variant="subtitle2" fontWeight={700} color="#0f172a">
                Messagerie, Flux & Échanges
              </Typography>
            </Stack>
            <Stack spacing={2}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Typography variant="caption" color="#64748b">
                  Total Conversations Actives
                </Typography>
                <Typography variant="body2" fontWeight={700}>
                  {stats.msg.conversations || 0}{' '}
                  <Box
                    component="span"
                    sx={{
                      fontWeight: 400,
                      color: '#94a3b8',
                      fontSize: '0.72rem',
                    }}
                  >
                    ({stats.msg.direct_conversations || 0} Dir •{' '}
                    {stats.msg.group_conversations || 0} Grp)
                  </Box>
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Typography variant="caption" color="#64748b">
                  Volume Global de Messages Envoyés
                </Typography>
                <Typography variant="body2" fontWeight={700}>
                  {stats.msg.messages || 0} messages
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Typography variant="caption" color="#64748b">
                  Taux d'Activité Général des Canaux
                </Typography>
                <Typography variant="body2" fontWeight={700} color="#7c3aed">
                  {stats.msg.active_conversations_rate || 0}% actifs
                </Typography>
              </Box>
              <Box
                sx={{
                  bgcolor: '#faf5ff',
                  p: 1.2,
                  borderRadius: 2,
                  textAlign: 'center',
                  mt: 1,
                }}
              >
                <Typography
                  sx={{
                    fontSize: '0.72rem',
                    color: '#6d28d9',
                    fontWeight: 600,
                  }}
                >
                  Intensité de messagerie : {stats.msg.messaging_intensity} pts
                </Typography>
              </Box>
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper
            sx={{
              p: 3,
              borderRadius: 4,
              bgcolor: '#ffffff',
              border: '1px solid #f1f5f9',
              height: '100%',
              boxSizing: 'border-box',
              boxShadow: 'none',
            }}
          >
            <Stack
              direction="row"
              spacing={1.5}
              alignItems="center"
              sx={{ mb: 2.5 }}
            >
              <Avatar
                sx={{ bgcolor: '#fff7ed', color: '#ea580c', borderRadius: 2 }}
              >
                <ContactSupport />
              </Avatar>
              <Box>
                <Typography
                  variant="subtitle2"
                  fontWeight={800}
                  color="#0f172a"
                >
                  Centre de Support Client ({stats.clients.total_contacts || 0}{' '}
                  contacts)
                </Typography>
                <Typography variant="caption" color="#64748b">
                  Répartition des tickets d'assistance entrants
                </Typography>
              </Box>
            </Stack>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: 2,
              }}
            >
              {(stats.clients.by_service_type || []).map((srv, index) => (
                <Box
                  key={index}
                  sx={{
                    bgcolor: '#fff8f2',
                    p: 1.8,
                    borderRadius: 2.5,
                    border: '1px solid #ffedd5',
                    textAlign: 'center',
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '0.68rem',
                      color: '#c2410c',
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      display: 'block',
                      mb: 0.5,
                    }}
                    noWrap
                  >
                    {srv.service_type}
                  </Typography>
                  <Typography variant="h5" fontWeight={800} color="#9a3412">
                    {srv.total}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="#c2410c"
                    sx={{ fontSize: '0.65rem' }}
                  >
                    Demandes
                  </Typography>
                </Box>
              ))}
            </Box>
          </Paper>
        </Grid>
          <Grid container spacing={3}>
  <Grid item xs={12} md={12}>
    <Paper
      sx={{
        p: 3,
        borderRadius: 4,
        bgcolor: '#ffffff',
        border: '1px solid #f1f5f9',
        boxSizing: 'border-box',
        boxShadow: 'none',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        minHeight: 250 // Donne la même hauteur confortable que le bloc de messagerie
      }}
    >
      {/* 1. En-tête du bloc (Identique à l'image) */}
      <Stack
        direction="row"
        spacing={1.5}
        alignItems="center"
        sx={{ mb: 3 }}
      >
        <Avatar
          sx={{ bgcolor: '#f5f3ff', color: '#7c3aed', borderRadius: 2, width: 38, height: 38 }}
        >
          <LocalLibrary sx={{ fontSize: 20 }} />
        </Avatar>
        <Typography
          variant="subtitle2"
          fontWeight={800}
          color="#0f172a"
          sx={{ fontSize: '0.95rem' }}
        >
          Capacitation & Administration des Guides
        </Typography>
      </Stack>

      {/* 2. Liste de Statistiques Alignées (Style chirurgical sans cadre) */}
      <Stack spacing={2.2} sx={{ width: '100%', mb: 3, px: 0.5 }}>
        
        {/* Ligne 1 : Total Guides */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 500 }}>
            Total des guides d'utilisation
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 800, color: '#0f172a' }}>
              {stats.learning.usage_guides || 0}
            </Typography>
            <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 500 }}>
              disponibles
            </Typography>
          </Box>
        </Box>

        {/* Ligne 2 : Format Distribution */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 500 }}>
            Format de Distribution
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 800, color: '#0f172a' }}>
            {stats.learning.youtube_guides || 0} <Box component="span" sx={{ color: '#94a3b8', fontWeight: 400, fontSize: '0.75rem' }}>YouTube</Box> • {stats.learning.upload_guides || 0} <Box component="span" sx={{ color: '#94a3b8', fontWeight: 400, fontSize: '0.75rem' }}>Envoi local</Box>
          </Typography>
        </Box>

        {/* Ligne 3 : Disponibilité active */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 500 }}>
            Disponibilité active du catalogue
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 800, color: '#7c3aed' }}>
            {stats.learning.active_rate || 0}% opérationnel
          </Typography>
        </Box>

      </Stack>

      {/* 3. Bandeau d'intensité violet tout en bas (Identique à l'image) */}
      <Box sx={{ 
        bgcolor: '#f5f3ff', 
        p: 1.2, 
        borderRadius: 2, 
        textAlign: 'center',
        width: '100%',
        boxSizing: 'border-box'
      }}>
        <Typography sx={{ fontSize: '0.75rem', color: '#6d28d9', fontWeight: 700, letterSpacing: '0.3px' }}>
          Ressources éducatives et documentations internes mises en ligne
        </Typography>
      </Box>

    </Paper>
  </Grid>
</Grid>
      </Grid>

  


    </Box>
  );
}
